// ===================================================
// ContactForm.jsx (Enhanced - EmailJS + Telegram + Mobile Field)
// ===================================================
import React, { useRef, useState } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import "../styles/Contact-form.css"

const ContactForm = () => {
  const formRef = useRef();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    honey: "", // 🐝 Honeypot
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const sendToTelegram = async ({ name, email, phone, message }) => {
    const token = process.env.REACT_APP_TELEGRAM_BOT_TOKEN;
    const chatId = process.env.REACT_APP_TELEGRAM_CHAT_ID;

    const text = `
📩 *New Contact Message*  
👤 Name: ${name}  
📧 Email: ${email}  
📱 Phone: ${phone}  
📝 Message: ${message}  
⏰ Time: ${new Date().toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      dateStyle: "medium",
      timeStyle: "short",
    })}
    `;

    try {
      await fetch(`https://api.telegram.org/bot${token}/sendMessage`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          chat_id: chatId,
          text,
          parse_mode: "Markdown",
        }),
      });
    } catch (err) {
      console.error("Telegram Error:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.honey !== "") {
      setSubmitStatus("spam");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // ✅ EmailJS
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        time: new Date().toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
          dateStyle: "medium",
          timeStyle: "short",
        }),
      };

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_USER_ID
      );

      // ✅ Telegram Bot
      await sendToTelegram(formData);

      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "", honey: "" });
    } catch (err) {
      console.error("Submit Error:", err);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus(null), 6000);
    }
  };

  return (
    <motion.form
      ref={formRef}
      onSubmit={handleSubmit}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="contact-form"
      aria-live="polite"
    >
      {/* Honeypot */}
      <input
        type="text"
        name="honey"
        value={formData.honey}
        onChange={handleChange}
        style={{ display: "none" }}
        tabIndex="-1"
        autoComplete="off"
      />

      {/* Name */}
      <motion.div className="form-group" whileHover={{ scale: 1.02 }}>
        <label htmlFor="name">Name</label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          autoComplete="name"
          placeholder="Your full name"
        />
      </motion.div>

      {/* Email */}
      <motion.div className="form-group" whileHover={{ scale: 1.02 }}>
        <label htmlFor="email">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
          placeholder="your@email.com"
        />
      </motion.div>

      {/* Phone */}
      <motion.div className="form-group" whileHover={{ scale: 1.02 }}>
        <label htmlFor="phone">Phone</label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          required
          autoComplete="tel"
          placeholder="+91 98765 43210"
          pattern="[0-9+\s-]{10,15}"
        />
      </motion.div>

      {/* Message */}
      <motion.div className="form-group" whileHover={{ scale: 1.02 }}>
        <label htmlFor="message">Message</label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          rows="5"
          required
          placeholder="What's on your mind?"
        />
      </motion.div>

      {/* Button */}
      <motion.button
        type="submit"
        disabled={isSubmitting}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="submit-btn"
        aria-disabled={isSubmitting}
      >
        {isSubmitting ? "Sending..." : "Send Message"}
      </motion.button>

      {/* Status */}
      {submitStatus === "success" && (
        <motion.div
          className="success-message"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          role="alert"
        >
          ✅ Message sent successfully! We'll get back to you soon.
        </motion.div>
      )}
      {submitStatus === "error" && (
        <motion.div
          className="error-message"
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          role="alert"
        >
          ❌ Failed to send. Please retry or contact us via{" "}
          <a
            href="https://wa.me/917824031201"
            target="_blank"
            rel="noopener noreferrer"
          >
            WhatsApp
          </a>.
        </motion.div>
      )}
    </motion.form>
  );
};

export default ContactForm;
